import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';

const SearchBar = ({ initialLocation = 'Kelowna', onSearch }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [selectedFromDate, setSelectedFromDate] = useState(null);
    const [selectedToDate, setSelectedToDate] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(initialLocation);

    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const CustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
        <button
            className="border rounded-full px-2 py-2 text-white bg-black w-[220px] text-left"
            onClick={onClick}
            ref={ref}
        >
            {value || placeholder}
        </button>
    ));

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const savedLocation = params.get('location') || initialLocation;
        setCurrentLocation(savedLocation);
    }, [location.search, initialLocation]);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const generateAllowedTimes = (baseDate) => {
        const allowedTimes = [];
        for (let hour = workingHoursStart; hour <= workingHoursEnd; hour++) {
            allowedTimes.push(new Date(baseDate).setHours(hour, 0, 0, 0)); // :00
            allowedTimes.push(new Date(baseDate).setHours(hour, 30, 0, 0)); // :30
        }
        return allowedTimes.map((time) => new Date(time));
    };

    const handleSearch = () => {
        if (!selectedFromDate || !selectedToDate) {
            alert('Please select both "From" and "To" dates and times.');
            return;
        }

        const fromDateTime = selectedFromDate;
        const toDateTime =
            selectedToDate.getTime() <= fromDateTime.getTime()
                ? new Date(fromDateTime.getTime() + 30 * 60 * 1000) // Default "To" to 30 minutes after "From"
                : selectedToDate;

        const startDateTime = fromDateTime.toISOString();
        const endDateTime = toDateTime.toISOString();

        if (onSearch) {
            onSearch(currentLocation, startDateTime, endDateTime);
        } else {
            navigate(
                `/results?location=${currentLocation}&start_date=${startDateTime}&end_date=${endDateTime}`
            );
        }
    };

    const isSearchEnabled =
        selectedFromDate &&
        selectedToDate &&
        selectedFromDate.getTime() <
            (selectedToDate.getTime() || new Date(selectedFromDate.getTime() + 30 * 60 * 1000).getTime());

    return (
        <div className="container mx-auto px-2">
            <div className="container mx-auto flex justify-start px-2 pt-2 my-4">
                <button
                    className={`px-6 py-2 rounded-full font-semibold ${
                        currentLocation === 'Kelowna' ? 'bg-white text-black' : 'bg-customGray text-black'
                    }`}
                    onClick={() => setCurrentLocation('Kelowna')}
                >
                    Kelowna <span className="font-light">YLW</span>
                </button>
                <button
                    className={`mx-2 px-6 py-2 rounded-full font-semibold ${
                        currentLocation === 'Vancouver' ? 'bg-white text-black' : 'bg-customGray text-black'
                    }`}
                    onClick={() => setCurrentLocation('Vancouver')}
                >
                    Vancouver <span className="font-light">YVR</span>
                </button>
            </div>
            <div
                className="container mx-auto relative h-[36rem] bg-cover bg-center rounded-2xl overflow-hidden transition-all duration-700 ease-in-out"
                style={{
                    backgroundImage:
                        currentLocation === 'Kelowna'
                            ? isSmallScreen
                                ? 'url("/kelowna-vertical.png")'
                                : 'url("/kelowna.jpg")'
                            : 'url("/vancouver.jpg")',
                }}
            >
                <div
                    className="container mx-auto md:bg-black rounded-full px-4 py-2 max-w-4xl mt-4 flex flex-col md:flex-row items-center justify-evenly space-y-4 md:space-y-0"
                >
                    {/* From Date Picker */}
                    <div className="flex flex-col items-center md:flex-row md:space-x-2 bg-black rounded-full p-4 md:p-0">
                        <span className="text-white font-semibold">From:</span>
                        <DatePicker
                            selected={selectedFromDate}
                            onChange={(date) => {
                                const defaultTime = new Date(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate(),
                                    0,
                                    0
                                );
                                setSelectedFromDate(
                                    date.getHours() ? date : defaultTime // Default to 9:00 AM if no time selected
                                );
                                setSelectedToDate(null); // Reset "To" when "From" changes
                            }}
                            placeholderText="Select a start date"
                            minDate={new Date()}
                            showTimeSelect
                            timeIntervals={30}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="border rounded-full px-2 py-2 text-white bg-black w-[220px]"
                            customInput={<CustomInput placeholder="Select a start date" />}
                        />
                    </div>
                    {/* To Date Picker */}
                    <div className="flex flex-col items-center md:flex-row md:space-x-2 bg-black rounded-full p-4 md:p-0">
                        <span className="text-white font-semibold">To:</span>
                        <DatePicker
                            selected={selectedToDate}
                            onChange={(date) => {
                                const minToTime = new Date(selectedFromDate.getTime() + 30 * 60 * 1000);
                                setSelectedToDate(date.getTime() <= minToTime.getTime() ? minToTime : date);
                            }}
                            placeholderText="Select an end date"
                            minDate={
                                selectedFromDate
                                    ? new Date(selectedFromDate.getTime() + 24 * 60 * 60 * 1000)
                                    : new Date().getTime()
                            }
                            showTimeSelect
                            timeIntervals={30}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            className="border rounded-full px-2 py-2 text-white bg-black w-[220px]"
                            customInput={<CustomInput placeholder="Select an end date" />}
                            disabled={!selectedFromDate}
                            portalId="root-portal"
                        />
                    </div>
                    {/* Search Button */}
                    <button
                        className={`p-6 rounded-full lg:p-2 ${
                            isSearchEnabled
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-gray-400 text-gray-300 cursor-not-allowed'
                        }`}
                        onClick={handleSearch}
                        disabled={!isSearchEnabled}
                    >
                        <MagnifyingGlassIcon className="h-6 w-6" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchBar;
